import { useState, useMemo, useCallback, useEffect, createContext, useContext } from 'react';
import Stack from '@mui/material/Stack';

import Iconify from 'src/components/iconify';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
// types
import { IGeneralObjectType } from 'src/compass/site/util';
import ICasEditInfoProps from './ICasEditInfoProps';
// components
import CasTable, { ICasTable } from './CasTable';
import CasEditableDialog from './CasEditableDialog';
import ICasTableColumn from './ICasTableColumn';
import ICasRenderCellProps from './ICasRenderCellProps';
import CasLink from '../CasLink/CasLink';
import CasButton from '../CasButton/CasButton';
import CasBaseDialog from '../CasDialog/CasBaseDialog';
import getCasTableCellValue from './getCasTableCellValue';

type TCasShowEdit = ({ row, column }: ICasRenderCellProps) => boolean;

type TCasEditRowInfo = {
  row: any | null;
  info?: ICasEditInfoProps | null;
};

interface ICasCRUDTableColumn  extends ICasTableColumn {
  // Convenience property. When true, makes cell link that opens edit dialog
  showEditOnclick?: boolean | TCasShowEdit;
  clickedCellFormInfo?: (row: any) => ICasEditInfoProps;
}

// One way of adding a record.  info overrides the table's entity / entityTitle / editForm.
export interface ICasCRUDAddChoice {
  label: string;
  info: Partial<ICasEditInfoProps>;
}

interface ICasCRUDTable  extends Omit<ICasTable, 'update' | 'entity'> {
  entityTitle: string;
  // When set, Add first asks which of these to add
  addChoices?: ICasCRUDAddChoice[];
  entity: string;
  editForm: any;
  deleteLabelSuffix?: string;
  canDelete?: boolean;
  columns: ICasCRUDTableColumn [];
  showAddButton?: boolean;
  showEditSingletonButton?: boolean;
  editFormAdditionalData?: IGeneralObjectType;
  reloadTable?: Date | null;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

// Context for dialog updates
const DialogDataUpdateContext = createContext<{
  onDialogUpdatedData: () => void;
}>({
  onDialogUpdatedData: () => {},
});

// Hook to use the Dialog Context
export const useDialogDataUpdate = () => useContext(DialogDataUpdateContext);

export default function CasCRUDTable({
  entityTitle,
  editForm,
  entity,
  deleteLabelSuffix = '',
  canDelete = true,
  columns: parmColumns,
  showAddButton = true,
  showEditSingletonButton = false,
  toolbarButtons: parmToolbarButtons = [],
  editFormAdditionalData = {},
  reloadTable = null,
  maxWidth = 'sm',
  addChoices,
  ...rest
}: ICasCRUDTable ) {
  const dialog = useCasBoolean();
  const addChoiceDialog = useCasBoolean();

  const [editRowInfo, setEditRowInfo] = useState<TCasEditRowInfo>({
    row: null,
    info: {
      id: null,
      editForm: null,
      entity: null,
      entityTitle: null,
      deleteLabelSuffix: null,
      canDelete: true,
    },
  });

  const [update, setUpdate] = useState<Date | null>(null);

  useEffect(() => {
    if (reloadTable) setUpdate(new Date());
  }, [reloadTable]);

  const onChanged = () => {
    setUpdate(new Date());
    onClose();
  };

  const onClose = () => {
    dialog.setFalse();
  };

  /*
     CasCRUDTable includes an extra showEditOnclick column property.
     If set for a column, we need to adjust the columns sent to CasTable
  */
  const adjustedColumns = useMemo(
    () =>
      parmColumns.map((parmColumn) => {
        if (!parmColumn.showEditOnclick) return parmColumn;

        const origRender = parmColumn.render;

        // When showEditOnclick is enabled, need to specify custom render
        return {
          ...parmColumn,
          render: ({ row, column }: ICasRenderCellProps) => {
            const { showEditOnclick } = column as ICasCRUDTableColumn ;

            const showLink =
              showEditOnclick instanceof Function
                ? showEditOnclick({ row, column })
                : showEditOnclick;

            const Value = ({ row: R, column: C }: ICasRenderCellProps) =>
              origRender ? origRender({ row: R, column: C }) : getCasTableCellValue({row: R, column: C});

            return showLink ? (
              <CasLink
                onClick={() => {
                  setEditRowInfo({
                    row,
                    info: parmColumn.clickedCellFormInfo
                      ? parmColumn.clickedCellFormInfo(row)
                      : null,
                  });
                  dialog.setTrue();
                }}
              >
                {Value({ row, column })}
              </CasLink>
            ) : (
              Value({ row, column })
            );
          },
        };
      }),
    [parmColumns, dialog]
  );

  const [data, setData] = useState(null);
  const [isPending, setIsPending] = useState(true);

  const handleAddAction = useCallback(() => {
    if (addChoices?.length) {
      addChoiceDialog.setTrue();
      return;
    }
    setEditRowInfo({ row: null, info: null });
    dialog.setTrue();
  }, [dialog, addChoiceDialog, addChoices]);

  const handleAddChoice = (choice: ICasCRUDAddChoice) => {
    addChoiceDialog.setFalse();
    setEditRowInfo({ row: null, info: choice.info as ICasEditInfoProps });
    dialog.setTrue();
  };

  // Data is only currently needed for Edit button
  const handleEditAction = useCallback(() => {
    setEditRowInfo({ row: data, info: null });
    dialog.setTrue();
  }, [dialog, data]);

  const adjustedButtons = useMemo(
    () => [
      ...parmToolbarButtons,

      ...(showAddButton
        ? [
            <CasButton
              onClick={handleAddAction}
              startIcon={<Iconify icon="mingcute:add-line" />}
              key="add"
            >
              Add
            </CasButton>,
          ]
        : []),

      ...(showEditSingletonButton
        ? [
            <CasButton
              onClick={handleEditAction}
              disabled={isPending}
              startIcon={<Iconify icon="material-symbols:edit" />}
              key="edit"
            >
              Edit
            </CasButton>,
          ]
        : []),
    ],
    [
      parmToolbarButtons,
      handleAddAction,
      handleEditAction,
      showAddButton,
      showEditSingletonButton,
      isPending,
    ]
  );

  const onChildDelete = () => setUpdate(new Date());

  const valueObj = useMemo(() => ({ onDialogUpdatedData: onChildDelete }), []);

  const sendAdditionalData = () => {
    if (editFormAdditionalData instanceof Function)
         return editFormAdditionalData(editRowInfo.row);
    return editFormAdditionalData;
  }

  return (
    <DialogDataUpdateContext.Provider value={valueObj}>
      <CasTable
        entity={entity}
        columns={adjustedColumns}
        {...rest}
        toolbarButtons={adjustedButtons}
        setData={
          // Data is only currently needed for Edit button
          showEditSingletonButton ? setData : undefined
        }
        setIsPending={
          // IsPending is only currently needed for Edit button
          showEditSingletonButton ? setIsPending : undefined
        }
        update={update}
      />
      {addChoiceDialog.value && addChoices && (
        <CasBaseDialog
          open
          title={`Add ${entityTitle}`}
          onClose={addChoiceDialog.setFalse}
          showCloseIcon
          maxWidth="xs"
        >
          <Stack spacing={2} sx={{ pt: 1 }}>
            {addChoices.map((choice) => (
              <CasButton key={choice.label} size="large" fullWidth onClick={() => handleAddChoice(choice)}>
                {choice.label}
              </CasButton>
            ))}
          </Stack>
        </CasBaseDialog>
      )}
      {dialog.value && (
        <CasEditableDialog
          entityTitle={editRowInfo?.info?.entityTitle ?? entityTitle}
          maxWidth={maxWidth}
          entity={editRowInfo?.info?.entity ?? entity}
          deleteLabelSuffix={editRowInfo?.info?.deleteLabelSuffix ?? deleteLabelSuffix}
          editForm={editRowInfo?.info?.editForm ?? editForm}
          dialog={dialog}
          id={editRowInfo.info?.id ?? editRowInfo.row?.id}
          onChanged={onChanged}
          canDelete={editRowInfo?.info?.canDelete ?? canDelete}
          editFormAdditionalData={sendAdditionalData()}
        />
      )}
    </DialogDataUpdateContext.Provider>
  );
}
